import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTOSAVE_DELAY_MS } from '../constants';
import { formatRelativeTime } from '../utils/storyHelpers';

/**
 * Modal open/close state
 */
export function useModalState(initial = false) {
  const [isOpen, setIsOpen] = useState(initial);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  return [isOpen, open, close];
}

/**
 * Debounced autosave: call `save(value)` AUTOSAVE_DELAY_MS after the last
 * `schedule(value)` call. Exposes a status string for a "Saved" indicator.
 */
export function useAutosave(save) {
  const [status, setStatus] = useState('idle'); // idle | pending | saving | saved
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const schedule = useCallback((value) => {
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStatus('saving');
      saveRef.current(value);
      setSavedAt(Date.now());
      setStatus('saved');
    }, AUTOSAVE_DELAY_MS);
  }, []);

  const flush = useCallback((value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('saving');
    saveRef.current(value);
    setSavedAt(Date.now());
    setStatus('saved');
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { status, savedAt, schedule, flush };
}

/**
 * Relative-time label that keeps itself fresh ("just now" -> "2 min ago")
 */
export function useRelativeTime(timestamp, intervalMs = 15000) {
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!timestamp) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, [timestamp, intervalMs]);
  return formatRelativeTime(timestamp);
}

/**
 * Focus Mode toggle, with Escape-to-exit support
 */
export function useFocusMode() {
  const [isFocusMode, setIsFocusMode] = useState(false);
  const toggle = useCallback(() => setIsFocusMode((v) => !v), []);
  const exit = useCallback(() => setIsFocusMode(false), []);

  useEffect(() => {
    if (!isFocusMode) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') exit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFocusMode, exit]);

  return { isFocusMode, toggle, exit };
}
